import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import { Select } from '@gpstrackit/gtc-react-controls';
import SaveIcon from '@mui/icons-material/Save';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {
  fetchZoneLayouts,
  fetchSettings,
  saveSettings,
  updateLocalSettings,
  revertSettings,
  resetSettings,
  clearSaveSuccess,
} from './redux/slice';
import './styles.scss';

// Default settings shape for fallback values
const DEFAULT_SETTINGS = {
  defaultZoneLayoutId: '',
  imageRetention: -1,
  inspectionRetention: -1,
};

// Retention periods in months (-1 = indefinite)
// Labels are message keys that will be resolved in the component
const RETENTION_PERIODS = [
  { labelKey: 'settings_retention_1_year', fallback: '1 Year', value: 12 },
  { labelKey: 'settings_retention_2_years', fallback: '2 Years', value: 24 },
  { labelKey: 'settings_retention_3_years', fallback: '3 Years', value: 36 },
  { labelKey: 'settings_retention_5_years', fallback: '5 Years', value: 60 },
  { labelKey: 'settings_retention_indefinite', fallback: 'Indefinite', value: -1 },
];

const SettingsPx = () => {
  const { messages } = useIntl();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.securityRedux.user);

  // Access state directly in the UI
  const zoneLayouts = useSelector((state) => state.accountSettingsRedux?.zoneLayouts || []);
  const settings = useSelector((state) => state.accountSettingsRedux?.settings || DEFAULT_SETTINGS);
  const originalSettings = useSelector((state) => state.accountSettingsRedux?.originalSettings || DEFAULT_SETTINGS);
  const loading = useSelector((state) => state.accountSettingsRedux?.loading || false);
  const saving = useSelector((state) => state.accountSettingsRedux?.saving || false);
  const saveSuccess = useSelector((state) => state.accountSettingsRedux?.saveSuccess || false);
  const settingsExist = useSelector((state) => state.accountSettingsRedux?.settingsExist || false);
  const error = useSelector((state) => state.accountSettingsRedux?.error || null);

  // Local UI state
  const [successMessage, setSuccessMessage] = useState('');

  // Build retention options with resolved labels
  const retentionOptions = RETENTION_PERIODS.map((period) => ({
    ...period,
    label: messages[period.labelKey] || period.fallback,
  }));
  const [errorMessage, setErrorMessage] = useState('');

  const hasChanges = JSON.stringify(settings) !== JSON.stringify(originalSettings);

  // Load settings on mount
  useEffect(() => {
    if (user?.companyId) {
      dispatch(fetchSettings());
      dispatch(fetchZoneLayouts());
    }
    return () => {
      dispatch(resetSettings());
    };
  }, []);

  // Sync error from Redux to local state
  useEffect(() => {
    if (error) {
      setErrorMessage(error);
    }
  }, [error]);

  // Handle save success
  useEffect(() => {
    if (saveSuccess) {
      setSuccessMessage(messages['settings_saved'] || 'Settings saved successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
      dispatch(clearSaveSuccess());
    }
  }, [saveSuccess, messages]);

  const handleSettingChange = (key, value) => {
    setErrorMessage('');
    dispatch(updateLocalSettings({ [key]: value }));
  };

  const handleSaveSettings = () => {
    if (!user?.companyId) {
      setErrorMessage(messages['settings_user_not_available'] || 'User information not available');
      return;
    }

    setErrorMessage('');
    setSuccessMessage('');

    dispatch(
      saveSettings({
        settings,
        settingsExist,
        userName: user?.userName,
      }),
    );
  };

  const handleResetSettings = () => {
    setErrorMessage('');
    dispatch(revertSettings());
  };

  if (loading) {
    return (
      <Container maxWidth="lg" className="settings-container">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" className="settings-container">
      <Box className="settings-header">
        <Typography variant="h4" component="h1" className="settings-title">
          {messages['settings_title']}
        </Typography>
      </Box>

      {successMessage && (
        <Alert
          severity="success"
          onClose={() => setSuccessMessage('')}
          sx={{ mb: 3, borderRadius: 1 }}
        >
          {successMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert
          severity="error"
          onClose={() => setErrorMessage('')}
          sx={{ mb: 3, borderRadius: 1 }}
        >
          {errorMessage}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Default Zone Layout */}
        <Grid item xs={12} md={6}>
          <Card className="settings-card">
            <CardContent>
              <Typography variant="h6" className="card-title">
                {messages['settings_default_zone_layout'] || 'Default Zone Layout'}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                {messages['settings_default_zone_layout_desc'] ||
                  'Select the default zone layout for new inspections.'}
              </Typography>

              <Select
                id="zone-layout-select"
                label={messages['settings_zone_layout'] || 'Zone Layout'}
                placeholder={messages['settings_none'] || 'None'}
                value={zoneLayouts.find((l) => l.id === settings.defaultZoneLayoutId) || null}
                onChange={(selected) => handleSettingChange('defaultZoneLayoutId', selected?.id || '')}
                options={zoneLayouts}
                textField="name"
                valueField="id"
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Box className="settings-actions">
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSaveSettings}
          disabled={!hasChanges || saving}
          size="large"
        >
          {saving ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              {messages['settings_saving'] || 'Saving...'}
            </>
          ) : (
            messages['settings_save'] || 'Save Settings'
          )}
        </Button>
        <Button
          variant="outlined"
          startIcon={<RestartAltIcon />}
          onClick={handleResetSettings}
          disabled={!hasChanges || saving}
          size="large"
        >
          {messages['settings_undo'] || 'Undo'}
        </Button>
      </Box>
    </Container>
  );
};

export default SettingsPx;
